
import React, { useState } from 'react';
import { Question, Answer, AnswerType } from '@/types/assessment';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, MoveVertical, BadgeDollarSign, Edit2, Trash, Eye, EyeOff } from 'lucide-react';
import { QuestionAnswers } from './QuestionAnswers';
import { DeleteQuestionDialog } from './DeleteQuestionDialog';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SimplifiedRichTextEditor } from '@/components/ui/simplified-rich-text-editor';

interface QuestionItemProps {
  categoryId: string;
  question: Question;
  onUpdateQuestion: (categoryId: string, questionId: string, updates: Partial<Question>) => void;
  onUpdateAnswer: (categoryId: string, questionId: string, answerId: string, updates: Partial<Answer>) => void;
  onAddAnswer: (categoryId: string, questionId: string) => void;
  onEditQuestion?: (questionId: string) => void;
  onDeleteQuestion?: (categoryId: string, questionId: string) => void;
  onToggleQuestionStatus?: (categoryId: string, questionId: string) => void;
  isMaturityModel?: boolean;
}

export function QuestionItem({
  categoryId,
  question,
  onUpdateQuestion,
  onUpdateAnswer,
  onAddAnswer,
  onEditQuestion,
  onDeleteQuestion,
  onToggleQuestionStatus,
  isMaturityModel = false,
}: QuestionItemProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleAnswerTypeChange = (value: string) => {
    const newType = value as AnswerType;
    
    // Special handling for maturity type
    if (newType === 'maturity') {
      // Create standard maturity level answers if changing to maturity type
      const maturityAnswers: Answer[] = [
        { id: crypto.randomUUID(), text: 'Initial - Ad hoc processes', value: 0 },
        { id: crypto.randomUUID(), text: 'Repeatable - Basic processes established', value: 1 },
        { id: crypto.randomUUID(), text: 'Defined - Processes documented and standardized', value: 2 },
        { id: crypto.randomUUID(), text: 'Managed - Processes measured and controlled', value: 3 },
        { id: crypto.randomUUID(), text: 'Optimizing - Continuous process improvement', value: 4 },
      ];
      
      onUpdateQuestion(categoryId, question.id, { 
        answerType: newType,
        answers: maturityAnswers
      });
    } else {
      // Just update the answer type for other types
      onUpdateQuestion(categoryId, question.id, { answerType: newType });
    }
  };

  const handleDeleteConfirm = () => {
    if (onDeleteQuestion) {
      onDeleteQuestion(categoryId, question.id);
    }
    setShowDeleteDialog(false);
  };

  const handleDisableInstead = () => {
    if (onToggleQuestionStatus) {
      onToggleQuestionStatus(categoryId, question.id);
    }
    setShowDeleteDialog(false);
  };

  const isDisabled = question.disabled || false;

  return (
    <div ref={setNodeRef} style={style} className="mb-4">
      <Collapsible className={`border rounded-lg ${question.premium ? 'border-l-4 border-l-[#9b87f5]' : ''} ${isDisabled ? 'opacity-60 bg-muted/30' : ''}`}>
        <CollapsibleTrigger className="flex w-full items-center justify-between p-4 text-left hover:bg-accent rounded-t-lg">
          <div className="flex items-center gap-2 font-medium">
            <div 
              className="cursor-move p-1 rounded hover:bg-gray-100 touch-none" 
              {...attributes}
              {...listeners}
            >
              <MoveVertical className="h-4 w-4 text-muted-foreground" />
            </div>
            {question.premium && (
              <BadgeDollarSign className="h-4 w-4 text-[#9b87f5]" />
            )}
            {isDisabled && (
              <span className="bg-muted text-muted-foreground px-2 py-1 text-xs rounded">
                Disabled
              </span>
            )}
            <span className={isDisabled ? 'line-through text-muted-foreground' : ''}>
              {question.text || 'Unnamed Question'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* Disable/Enable Button */}
            {onToggleQuestionStatus && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleQuestionStatus(categoryId, question.id);
                }}
                className="h-8 w-8 p-0"
                title={isDisabled ? 'Enable question' : 'Disable question'}
              >
                {isDisabled ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            )}
            
            {/* Edit Button */}
            {onEditQuestion && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditQuestion(question.id);
                }}
                className="h-8 w-8 p-0"
                title="Edit question"
              >
                <Edit2 className="h-4 w-4" />
              </Button>
            )}
            
            {/* Delete Button */}
            {onDeleteQuestion && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteDialog(true);
                }}
                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                title="Delete question"
              >
                <Trash className="h-4 w-4" />
              </Button>
            )}
            
            <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
              {question.answerType}
            </span>
            <ChevronDown className="h-4 w-4" />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="p-4 border-t space-y-4">
            <div className="space-y-2">
              <Label htmlFor={`question-${question.id}`}>Question Text</Label>
              <Input
                id={`question-${question.id}`}
                value={question.text}
                onChange={(e) => onUpdateQuestion(categoryId, question.id, { text: e.target.value })}
                placeholder="Enter question text"
                disabled={isDisabled}
              />
            </div>
            
            {/* Rich Text Explanation Field */}
            <div className="space-y-2">
              <Label htmlFor={`question-explanation-${question.id}`}>Question Explanation</Label>
              <SimplifiedRichTextEditor
                value={question.explanation || ''}
                onChange={(value) => onUpdateQuestion(categoryId, question.id, { explanation: value })}
                placeholder="Provide additional context or explanation for this question (optional)"
              />
              <p className="text-xs text-muted-foreground">
                This explanation will be shown to users when answering the question
              </p>
            </div>

            <div className="flex gap-4">
              <div className="flex-1 space-y-2">
                <Label htmlFor={`question-type-${question.id}`}>Answer Type</Label>
                <Select
                  value={question.answerType}
                  onValueChange={handleAnswerTypeChange}
                  disabled={isMaturityModel || isDisabled}
                >
                  <SelectTrigger id={`question-type-${question.id}`}>
                    <SelectValue placeholder="Select answer type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Single Choice</SelectItem>
                    <SelectItem value="multiple">Multiple Choice</SelectItem>
                    <SelectItem value="slider">Slider</SelectItem>
                    <SelectItem value="maturity">Maturity Levels</SelectItem>
                  </SelectContent>
                </Select>
                {isMaturityModel && (
                  <p className="text-xs text-purple-600">
                    Answer type is fixed for maturity model questions.
                  </p>
                )}
              </div>
              
              <div className="flex items-end gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id={`question-required-${question.id}`} 
                    checked={question.required}
                    onCheckedChange={(checked) => 
                      onUpdateQuestion(
                        categoryId, 
                        question.id, 
                        { required: checked === true }
                      )
                    }
                    disabled={isDisabled}
                  />
                  <Label htmlFor={`question-required-${question.id}`}>Required</Label>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between border p-3 rounded-md bg-[#F1F0FB]/50">
              <div className="flex items-center gap-2">
                <BadgeDollarSign className="h-5 w-5 text-[#9b87f5]" />
                <div>
                  <p className="text-sm font-medium">Premium Question</p>
                  <p className="text-xs text-muted-foreground">Only available to logged-in users</p>
                </div>
              </div>
              <Switch 
                checked={question.premium} 
                onCheckedChange={(checked) => 
                  onUpdateQuestion(
                    categoryId, 
                    question.id, 
                    { premium: checked }
                  )
                }
                disabled={isDisabled}
              />
            </div>

            {!isDisabled && (
              <QuestionAnswers
                categoryId={categoryId}
                questionId={question.id}
                answers={question.answers}
                answerType={question.answerType}
                onAddAnswer={onAddAnswer}
                onUpdateAnswer={onUpdateAnswer}
              />
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Delete Confirmation Dialog */}
      <DeleteQuestionDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        onDisableInstead={handleDisableInstead}
        questionText={question.text}
      />
    </div>
  );
}
